/** sessionStorage keys — latest Native Auth /oauth2/v2.0/token response (OTP, etc.). */
export const ENTRA_NATIVE_REFRESH_STASH_KEY = 'he_entra_native_refresh_token';
const ENTRA_NATIVE_ID_TOKEN_STASH_KEY = 'he_entra_native_id_token_stash';

function isEntraTokenEndpoint(url: string): boolean {
  return /\/oauth2\/v2\.0\/token(?:\?|$)/i.test(url);
}

function stashFromTokenJson(json: Record<string, unknown>): void {
  const refresh =
    (typeof json['refresh_token'] === 'string' && json['refresh_token']) ||
    (typeof json['refreshToken'] === 'string' && json['refreshToken']);
  if (refresh && String(refresh).trim().length > 20) {
    sessionStorage.setItem(ENTRA_NATIVE_REFRESH_STASH_KEY, String(refresh).trim());
  }
  const idToken =
    (typeof json['id_token'] === 'string' && json['id_token']) ||
    (typeof json['idToken'] === 'string' && json['idToken']);
  if (typeof idToken === 'string' && idToken.startsWith('eyJ')) {
    sessionStorage.setItem(ENTRA_NATIVE_ID_TOKEN_STASH_KEY, idToken);
  }
}

async function captureTokenResponse(response: Response): Promise<void> {
  if (!response.ok) {
    return;
  }
  try {
    const json = (await response.json()) as Record<string, unknown>;
    stashFromTokenJson(json);
  } catch {
    // ignore non-JSON
  }
}

function resolveFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
}

declare global {
  interface Window {
    __heEntraTokenCaptureInstalled?: boolean;
  }
}

/**
 * MSAL Custom Auth receives refresh_token on /token but does not always expose it
 * on AccountData. Stash it from the HTTP response for Aya handoff + session/refresh.
 */
export function installEntraNativeAuthTokenCapture(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__heEntraTokenCaptureInstalled) {
    return;
  }
  window.__heEntraTokenCaptureInstalled = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const response = await nativeFetch(input, init);
    try {
      const url = resolveFetchUrl(input);
      if (isEntraTokenEndpoint(url)) {
        await captureTokenResponse(response.clone());
      }
    } catch {
      // ignore
    }
    return response;
  };

  const xhrProto = XMLHttpRequest.prototype;
  const nativeOpen = xhrProto.open;
  const nativeSend = xhrProto.send;

  xhrProto.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ) {
    (this as XMLHttpRequest & { __heEntraUrl?: string }).__heEntraUrl = String(url);
    return nativeOpen.call(this, method, url, async ?? true, username, password);
  };

  xhrProto.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    this.addEventListener('load', function () {
      const reqUrl =
        (this as XMLHttpRequest & { __heEntraUrl?: string }).__heEntraUrl || '';
      if (!isEntraTokenEndpoint(reqUrl) || this.status < 200 || this.status >= 300) {
        return;
      }
      try {
        const json = JSON.parse(this.responseText) as Record<string, unknown>;
        stashFromTokenJson(json);
      } catch {
        // ignore
      }
    });
    return nativeSend.call(this, body);
  };
}

export function getStashedEntraRefreshToken(): string {
  try {
    return sessionStorage.getItem(ENTRA_NATIVE_REFRESH_STASH_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

export function getStashedEntraIdToken(): string {
  try {
    return sessionStorage.getItem(ENTRA_NATIVE_ID_TOKEN_STASH_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

export function clearStashedEntraNativeTokens(): void {
  try {
    sessionStorage.removeItem(ENTRA_NATIVE_REFRESH_STASH_KEY);
    sessionStorage.removeItem(ENTRA_NATIVE_ID_TOKEN_STASH_KEY);
  } catch {
    // ignore
  }
}
